import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Anchor,
  MapPin, 
  User, 
  Phone,
  Clock,
  Navigation,
  Loader2,
  AlertCircle,
  CheckCircle2,
  StopCircle,
  Ship,
  Play,
  Banknote,
  CreditCard,
  Flag,
  Map,
  ExternalLink,
  Car
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ChatWidget from "@/components/ChatWidget";
import { useWhatsAppBubble } from "@/components/WhatsAppBubble";

import { API_URL } from "@/config/api";

// Stable Map component for driver view - doesn't re-render on position updates
function DriverRouteMap({ pickupAddress, currentPosition }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const clientMarkerRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Initialize map only once
  useEffect(() => {
    if (isInitializedRef.current) return;
    
    // Load Leaflet CSS
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Load Leaflet JS and initialize map
    const initMap = async () => {
      if (!window.L) {
        await new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.onload = resolve;
          document.head.appendChild(script);
        });
      }

      if (mapRef.current && !mapInstanceRef.current) {
        // Default center (Le Havre port area)
        const defaultLat = 49.4944;
        const defaultLng = 0.1079;

        const map = window.L.map(mapRef.current, {
          zoomControl: true,
          scrollWheelZoom: true
        }).setView([defaultLat, defaultLng], 13);
        
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OSM'
        }).addTo(map);

        mapInstanceRef.current = map;
        isInitializedRef.current = true;

        // Geocode the pickup address to get client location
        if (pickupAddress) {
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(pickupAddress)}&limit=1`
            );
            const data = await response.json();
            
            if (data && data[0]) {
              const clientLat = parseFloat(data[0].lat);
              const clientLng = parseFloat(data[0].lon);
              
              // Create client marker icon
              const clientIcon = window.L.divIcon({
                html: `<div style="background: #F97316; width: 36px; height: 36px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center;">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="1">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>`,
                className: '',
                iconSize: [36, 36],
                iconAnchor: [18, 36]
              });
              
              clientMarkerRef.current = window.L.marker([clientLat, clientLng], { icon: clientIcon })
                .addTo(map)
                .bindPopup(`<b>📍 Client Pickup</b><br/>${pickupAddress}`);
              
              // Fit bounds to show both markers if driver position available
              if (currentPosition) {
                const bounds = window.L.latLngBounds([
                  [clientLat, clientLng],
                  [currentPosition.latitude, currentPosition.longitude]
                ]);
                map.fitBounds(bounds, { padding: [30, 30] });
              } else {
                map.setView([clientLat, clientLng], 14);
              }
            }
          } catch (error) {
            console.log('Geocoding error:', error);
          }
        }
      }
    };

    initMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        isInitializedRef.current = false;
        driverMarkerRef.current = null;
        clientMarkerRef.current = null;
      }
    };
  }, [pickupAddress]); // Only depend on pickupAddress for init

  // Update driver position marker without recreating the map
  useEffect(() => {
    if (!mapInstanceRef.current || !currentPosition) return;

    const map = mapInstanceRef.current;
    
    // Create driver icon
    const driverIcon = window.L.divIcon({
      html: `<div style="background: #3B82F6; width: 28px; height: 28px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center;">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18.4 10.5l-2.1-3.2c-.3-.5-.9-.8-1.5-.8H9.2c-.6 0-1.2.3-1.5.8l-2.1 3.2-2.1.6C2.7 11.3 2 12.1 2 13v3c0 .6.4 1 1 1h2"/>
          <circle cx="7" cy="17" r="2"/>
          <circle cx="17" cy="17" r="2"/>
        </svg>
      </div>`,
      className: '',
      iconSize: [28, 28],
      iconAnchor: [14, 14]
    });

    // Update or create driver marker
    if (driverMarkerRef.current) {
      driverMarkerRef.current.setLatLng([currentPosition.latitude, currentPosition.longitude]);
    } else {
      driverMarkerRef.current = window.L.marker(
        [currentPosition.latitude, currentPosition.longitude], 
        { icon: driverIcon }
      ).addTo(map).bindPopup('🚗 Your position');
    }

    // Center map on driver (only first time or if far from center)
    const center = map.getCenter();
    const distance = Math.abs(center.lat - currentPosition.latitude) + Math.abs(center.lng - currentPosition.longitude);
    if (distance > 0.05) {
      map.setView([currentPosition.latitude, currentPosition.longitude], 14, { animate: true });
    }
  }, [currentPosition]);

  return (
    <div 
      ref={mapRef} 
      className="w-full h-48 rounded-lg overflow-hidden"
      style={{ zIndex: 0, position: 'relative' }}
    />
  );
}

export default function DriverTrackingPage() {
  const { token } = useParams();
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [updateCount, setUpdateCount] = useState(0);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [completingTrip, setCompletingTrip] = useState(false);
  const [tripCompleted, setTripCompleted] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const watchIdRef = useRef(null);
  const updateIntervalRef = useRef(null);
  const { setHideWhatsAppBubble } = useWhatsAppBubble();

  // Handle chat open state and update global WhatsApp bubble visibility
  const handleChatOpenChange = useCallback((isOpen) => {
    setIsChatOpen(isOpen);
    setHideWhatsAppBubble(isOpen);
  }, [setHideWhatsAppBubble]);

  // Cleanup: show WhatsApp bubble when leaving page
  useEffect(() => {
    return () => {
      setHideWhatsAppBubble(false);
    };
  }, [setHideWhatsAppBubble]);

  // Fetch tracking session data
  const fetchTrackingData = useCallback(async () => {
    if (!token) {
      setError("Invalid tracking link");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/tracking/driver/${token}`);
      setTrackingData(response.data);
      setError(null);
    } catch (err) {
      console.error("Tracking fetch error:", err);
      if (err.response?.status === 404) {
        setError("Ce lien de tracking est invalide ou a expiré.");
      } else if (err.response?.status === 410) {
        setError(err.response?.data?.detail || "Cette session de tracking est terminée.");
      } else {
        setError("Impossible de charger les informations. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTrackingData();
  }, [fetchTrackingData]);

  // Send position to server
  const sendPosition = useCallback(async (position) => {
    if (!trackingData?.booking_id) return;

    try {
      await axios.post(`${API_URL}/api/tracking/driver/${token}/update-location`, {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        heading: position.coords.heading,
        speed: position.coords.speed
      });
      
      setLastUpdate(new Date());
      setUpdateCount(prev => prev + 1);
      setCurrentPosition({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      });
    } catch (err) {
      console.error("Failed to send position:", err);
      // Don't show error toast for every failed update, just log it
    }
  }, [trackingData, token]);

  // Start sharing location
  const startSharing = () => {
    if (!navigator.geolocation) {
      toast.error("La géolocalisation n'est pas supportée par votre navigateur");
      return;
    }

    setIsSharing(true);
    toast.success("Partage de position activé !");

    // Watch position with high accuracy
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        sendPosition(position);
      },
      (error) => {
        console.error("Geolocation error:", error);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error("Permission de géolocalisation refusée. Veuillez l'autoriser dans les paramètres.");
          stopSharing();
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000
      }
    );

    // Also send position every 10 seconds even if not moving
    updateIntervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        sendPosition,
        (err) => console.error("Position update error:", err),
        { enableHighAccuracy: true, timeout: 5000 }
      );
    }, 10000);
  };

  // Stop sharing location and notify backend
  const stopSharing = async () => {
    // Clear local GPS watchers
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }
    setIsSharing(false);
    
    // Notify backend that driver paused tracking
    try {
      await axios.post(`${API_URL}/api/tracking/driver/${token}/pause`);
      toast.info("GPS sharing stopped - tracking paused");
    } catch (err) {
      console.error("Failed to notify backend of pause:", err);
      toast.warning("GPS stopped locally - backend may not be updated");
    }
  };

  // Confirm cash payment received
  const confirmPayment = async () => {
    setConfirmingPayment(true);
    try {
      await axios.post(`${API_URL}/api/tracking/driver/${token}/confirm-payment`);
      toast.success("Payment confirmed!");
      // Update local state to paid_by_cash
      setTrackingData(prev => ({ ...prev, payment_status: "paid_by_cash" }));
    } catch (err) {
      console.error("Payment confirmation error:", err);
      toast.error("Failed to confirm payment. Please try again.");
    } finally {
      setConfirmingPayment(false);
    }
  };

  // Complete the trip
  const completeTrip = async () => {
    setCompletingTrip(true);
    try {
      await axios.post(`${API_URL}/api/tracking/driver/${token}/complete-trip`);
      toast.success("Course terminée avec succès !");
      // Stop GPS sharing
      stopSharing();
      // Update local state
      setTripCompleted(true);
    } catch (err) {
      console.error("Trip completion error:", err);
      toast.error("Échec de la confirmation. Veuillez réessayer.");
    } finally {
      setCompletingTrip(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, []);

  // Format pickup time
  const formatPickupTime = () => {
    if (!trackingData?.pickup_datetime) return "N/A";
    try {
      const dt = new Date(trackingData.pickup_datetime);
      return dt.toLocaleString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return trackingData.pickup_datetime;
    }
  };

  // Check if payment is already done
  const isPaid = trackingData?.payment_status === "paid" || trackingData?.payment_status === "paid_by_cash";

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Tracking Unavailable</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-orange-500 hover:bg-orange-600">
                Go to Homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-sm border-b border-slate-700 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-md bg-orange-500 flex items-center justify-center">
                <Anchor className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-black text-white tracking-tight">Y GO</span>
            </Link>
            <div className="flex items-center gap-2 text-orange-400 text-sm font-semibold">
              <Navigation className="w-4 h-4" />
              <span>Driver Mode</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-lg">
        {/* Status Banner */}
        <div className={`mb-6 p-4 rounded-xl text-white shadow-lg ${isSharing ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-orange-500 to-orange-600'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 bg-white/20 rounded-full flex items-center justify-center ${isSharing ? 'animate-pulse' : ''}`}>
              {isSharing ? <Car className="w-7 h-7" /> : <MapPin className="w-7 h-7" />}
            </div>
            <div>
              <h1 className="text-xl font-bold">
                {isSharing ? "Trip in Progress" : "Ready to Start"}
              </h1>
              <p className="text-white/80 text-sm">
                {isSharing 
                  ? `Trip in progress • ${updateCount} update(s)` 
                  : "Start your trip to begin tracking"}
              </p>
            </div>
          </div>
        </div>

        {/* Main Action Button */}
        <Card className="mb-4 overflow-hidden shadow-xl">
          <CardContent className="p-6">
            {!isSharing ? (
              <>
                <div className="text-center mb-4">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Car className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2">
                    Start Trip
                  </h2>
                  <p className="text-slate-600 text-sm">
                    Begin your trip to pick up the client
                  </p>
                </div>
                <Button 
                  onClick={startSharing}
                  className="w-full h-14 text-lg font-bold bg-green-500 hover:bg-green-600"
                  data-testid="start-sharing-btn"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Start Trip
                </Button>
                <p className="text-xs text-slate-400 text-center mt-3">
                  <Navigation className="w-3 h-3 inline mr-1" />
                  GPS tracking will be automatically activated
                </p>
              </>
            ) : (
              <>
                {/* Map with route when trip is active */}
                <div className="mb-4">
                  <DriverRouteMap 
                    pickupAddress={trackingData?.pickup_address}
                    terminalName={trackingData?.terminal_name}
                    currentPosition={currentPosition}
                  />
                </div>

                {/* Status and navigation buttons */}
                <div className="text-center mb-4">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-green-600 font-semibold">Trip in Progress</span>
                  </div>
                  {currentPosition && (
                    <p className="text-slate-500 text-xs">
                      GPS: {currentPosition.latitude.toFixed(4)}, {currentPosition.longitude.toFixed(4)}
                      {lastUpdate && ` • Updated ${lastUpdate.toLocaleTimeString('en-GB')}`}
                    </p>
                  )}
                </div>

                {/* Navigation app buttons */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <Button
                    variant="outline"
                    className="h-12 flex items-center justify-center gap-2 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
                    onClick={() => {
                      const address = encodeURIComponent(trackingData?.pickup_address || '');
                      window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
                    }}
                    data-testid="open-google-maps"
                  >
                    <Map className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Google Maps</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12 flex items-center justify-center gap-2 border-purple-200 hover:bg-purple-50 hover:border-purple-300"
                    onClick={() => {
                      const address = encodeURIComponent(trackingData?.pickup_address || '');
                      // Waze deep link
                      window.open(`https://waze.com/ul?q=${address}&navigate=yes`, '_blank');
                    }}
                    data-testid="open-waze"
                  >
                    <Navigation className="w-5 h-5 text-purple-600" />
                    <span className="font-medium">Waze</span>
                  </Button>
                </div>

                <Button 
                  onClick={stopSharing}
                  variant="outline"
                  className="w-full h-12 text-red-600 border-red-300 hover:bg-red-50"
                  data-testid="stop-sharing-btn"
                >
                  <StopCircle className="w-5 h-5 mr-2" />
                  End Trip
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Payment Section */}
        <Card className="mb-4 shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              {isPaid ? (
                <CreditCard className="w-5 h-5 text-green-500" />
              ) : (
                <Banknote className="w-5 h-5 text-orange-500" />
              )}
              Payment
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isPaid ? (
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="font-bold text-green-700 text-lg">Payment Confirmed</p>
                <p className="text-green-600 text-sm mt-1">
                  {trackingData?.final_price ? `€${trackingData.final_price}` : ''} - {trackingData?.payment_status === "paid" ? "Paid Online" : "Paid by Cash"}
                </p>
              </div>
            ) : (
              <div className="text-center">
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 mb-4">
                  <p className="text-orange-600 text-sm mb-1">Amount to collect</p>
                  <p className="text-3xl font-bold text-orange-700">
                    €{trackingData?.final_price || 0}
                  </p>
                  <p className="text-orange-600 text-xs mt-1">Cash Payment</p>
                </div>
                <Button
                  onClick={confirmPayment}
                  disabled={confirmingPayment}
                  className="w-full h-12 bg-green-500 hover:bg-green-600 text-lg font-semibold"
                  data-testid="confirm-payment-btn"
                >
                  {confirmingPayment ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Banknote className="w-5 h-5 mr-2" />
                  )}
                  Confirm Payment Received
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Complete Trip Section */}
        <Card className="mb-4 shadow-lg border-2 border-blue-200">
          <CardHeader className="pb-3 bg-blue-50">
            <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
              <Flag className="w-5 h-5" />
              Fin de Course
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {tripCompleted ? (
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="font-bold text-green-700 text-lg">Course Terminée</p>
                <p className="text-green-600 text-sm mt-1">
                  Merci ! La course a été marquée comme complétée.
                </p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-slate-600 text-sm mb-4">
                  Une fois le passager déposé, confirmez la fin de la course.
                </p>
                <Button
                  onClick={completeTrip}
                  disabled={completingTrip || !isPaid}
                  className={`w-full h-14 text-lg font-semibold ${
                    isPaid 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-slate-300 cursor-not-allowed'
                  }`}
                  data-testid="complete-trip-btn"
                >
                  {completingTrip ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Flag className="w-5 h-5 mr-2" />
                  )}
                  Terminer la Course
                </Button>
                {!isPaid && (
                  <p className="text-orange-600 text-xs mt-2">
                    ⚠️ Veuillez d'abord confirmer le paiement
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Booking Details */}
        <Card className="shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Ship className="w-5 h-5 text-orange-500" />
              Trip Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Booking Reference */}
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-xs text-orange-600 font-medium">Reference</p>
              <p className="font-mono font-bold text-lg text-orange-700">{trackingData?.booking_id}</p>
            </div>

            {/* Passenger Info */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Passenger</p>
                <p className="font-medium text-slate-900">{trackingData?.passenger_name}</p>
                <a 
                  href={`tel:${trackingData?.passenger_phone}`}
                  className="text-sm text-blue-600 flex items-center gap-1 mt-1"
                >
                  <Phone className="w-3 h-3" />
                  {trackingData?.passenger_phone}
                </a>
              </div>
            </div>

            {/* Pickup Location */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pickup Location</p>
                <p className="font-medium text-slate-900">{trackingData?.terminal_name}</p>
                <p className="text-sm text-slate-600">{trackingData?.pickup_address}</p>
              </div>
            </div>

            {/* Pickup Time */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pickup Time</p>
                <p className="font-medium text-slate-900">{formatPickupTime()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help */}
        <div className="mt-6 text-center">
          <p className="text-slate-400 text-sm mb-2">Need help?</p>
          <a 
            href="tel:+33278780404" 
            className="inline-flex items-center gap-2 text-white bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg transition-colors"
          >
            <Phone className="w-4 h-4" />
            +33 2 78 78 04 04
          </a>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-xs">
          <p>Y GO - Seamen Shore Services</p>
          <p>Le Havre, France</p>
        </div>
      </div>

      {/* Chat Widget for Driver */}
      {trackingData?.booking_id && (
        <ChatWidget 
          bookingId={trackingData.booking_id}
          userType="driver"
          userName={trackingData.passenger_name}
          driverName={trackingData.driver_name}
          autoOpenOnMessage={true}
          onOpenChange={handleChatOpenChange}
        />
      )}
    </div>
  );
}
